import { ReactElement, useEffect, useMemo, useState } from "react";
import { t } from "@lingui/core/macro";

import { Modal, SubmitCancelButtons } from "_/components/modal";
import { Select } from "_/components/select";
import { Alert } from "_/components/alert";
import { useAddToast } from "_/components/toasts";
import {
  useAddPrintQueueEntry,
  usePrintQueueEntries,
} from "_/data/print-queue";
import { Project } from "_/data/projects";
import { Uuid } from "_/types";

import { FormRow, Label } from "./styled";

type AddToQueueModalProps = {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
};

export const AddToQueueModal = ({
  project,
  isOpen,
  onClose,
}: AddToQueueModalProps): ReactElement | null => {
  // const machinesQuery = useMachines();
  const queueEntriesQuery = usePrintQueueEntries();
  const addQueueEntry = useAddPrintQueueEntry();
  const [selectedRevisionId, setSelectedRevisionId] = useState<
    Uuid | undefined
  >(project.revisions.at(-1)?.id);
  // const [selectedMachineId, setSelectedMachineId] = useState<string>("");
  const addToast = useAddToast();

  useEffect(() => {
    setSelectedRevisionId(project.revisions.at(-1)?.id);
  }, [project.id, project.revisions]);

  // const machineOptions = [...(machinesQuery.data ?? [])]
  //   .sort((a, b) => a.name.localeCompare(b.name))
  //   .map((machine) => ({
  //     value: machine.id,
  //     label: machine.name,
  //   }));

  const revisionOptions = useMemo(() => {
    return project.revisions
      .toSorted((a, b) => b.number - a.number)
      .map((revision) => ({
        value: revision.id,
        label: t`common.revision-name ${revision.number}`,
      }));
  }, [project.revisions]);

  const selectedRevision = project.revisions.find(
    (revision) => revision.id === selectedRevisionId
  );

  const duplicateEntries = useMemo(
    () =>
      queueEntriesQuery.data?.filter(
        (entry) => entry.revisionId === selectedRevisionId
      ) ?? [],
    [queueEntriesQuery.data, selectedRevisionId]
  );

  const revisionWarning =
    duplicateEntries.length > 0 && selectedRevision
      ? t`components.queue.modal-warning ${selectedRevision.number}`
      : null;

  const handleAdd = async () => {
    if (!selectedRevisionId) return;
    await addQueueEntry.mutateAsync({
      revisionId: selectedRevisionId,
      // machineId: selectedMachineId || undefined,
    });
    addToast({
      kind: "success",
      title: t`components.queue.add-success`,
      timeout: 5_000,
    });
    onClose();
  };

  const isAddDisabled =
    addQueueEntry.isPending || !selectedRevisionId || !project.revisions.length;

  return (
    <Modal
      open={isOpen}
      onClose={addQueueEntry.isPending ? undefined : onClose}
      title={t`components.queue.modal-title`}
    >
      {revisionWarning && <Alert kind="warning">{revisionWarning}</Alert>}
      {/* Machine selection UI hidden for now */}
      {/* <FormRow>
          <Label>{t`components.queue.modal-machine`}</Label>
          <Select
            value={selectedMachineId}
            options={[
              { value: "", label: t`components.queue.machine-any` },
              ...machineOptions,
            ]}
            onChange={(value: string) => setSelectedMachineId(value)}
          />
        </FormRow> */}

      <FormRow>
        <Label>{t`components.queue.modal-revision`}</Label>
        <Select
          value={selectedRevisionId}
          options={revisionOptions}
          onChange={(value: Uuid) => setSelectedRevisionId(value)}
        />
      </FormRow>
      <SubmitCancelButtons
        submitButtonLabel={t`components.queue.add-button`}
        cancelButtonLabel={t`common.cancel`}
        onSubmit={handleAdd}
        onCancel={addQueueEntry.isPending ? undefined : onClose}
        disableSubmit={isAddDisabled}
        disableCancel={addQueueEntry.isPending}
      />
    </Modal>
  );
};
